import React, {
  Fragment,
  useEffect,
  useMemo,
} from 'react';
import isNil from 'lodash/isNil';
import compact from 'lodash/compact';
import {
  PatientMetaDataProps,
} from 'pages/Dashboard/pages/Encounters/pages/List/types/patientMetaData';
import {
  formatDate,
} from 'utils/date';
import {
  useEncounterDetails,
} from 'pages/Dashboard/pages/Encounters/pages/List/hooks';
import Skeleton from 'core/components/Skeleton';
import {
  normalizeContent,
  removeFieldLabelsFromHtml,
} from 'views/EMR/Settings/helper';
import {
  insurancesEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  useGetPatientInsurances,
} from 'views/EMR/components/Insurance/hooks';

type VisitDetailsItemProps = {
  value: string;
};

function VisitDetailsItem({ value }: VisitDetailsItemProps) {
  const normalizedValue = useMemo(
    () => removeFieldLabelsFromHtml(normalizeContent(value, false)) ?? '',
    [value],
  );
  return (
    <div
      className="text-xs h-full overflow-y-auto ck-content"
      // oxlint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: normalizedValue }}
    />
  );
}

export default function VisitDetails({ encounter }: PatientMetaDataProps) {
  const { encounterId, patient } = encounter ?? {};
  const {
    data,
    isFetching,
  } = useEncounterDetails(encounterId ?? 0);

  const {
    data: insuranceList,
    isLoading: loadingInsurances,
    refetch: refetchInsurances,
  } = useGetPatientInsurances(patient?.patientId ?? 0);

  useEffect(() => {
    const unsubscribe = insurancesEventChannel.on('onPatientInsuranceUpdated', async (_patientId) => {
      if (_patientId === patient?.patientId) {
        await refetchInsurances();
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const {
    lastVisitDate,
    todaysHPI,
    previousAssessmentCodes,
    previousPlan,
  } = data ?? {};

  const hpiElements = useMemo(() => todaysHPI?.hpiFromIntakeElements ?? [], [todaysHPI]);

  const hpiUpdates = useMemo(
    () => hpiElements.map(
      (hpi) => `${hpi.title}: ${hpi.hpiAttributes?.join(', ')}`,
    ) ?? ['N/A'],
    [hpiElements],
  );

  const updates = useMemo(() => [{
    label: 'Plan',
    values: [previousPlan ?? 'N/A'],
  }, {
    label: 'Assessment',
    values: previousAssessmentCodes
      ?.map(({ code, description }) => `${code} - ${description}`) ?? [],
  }], [previousPlan, previousAssessmentCodes]);

  const showPreviousDetails = (previousPlan ?? '').length > 0 || (previousAssessmentCodes ?? []).length > 0;

  return (
    <div className="flex flex-col w-full px-4 gap-4 [word-break:break-word]">
      <div className="flex flex-col gap-2">
        <h2 className="font-semibold">Reason for visit</h2>
        <span className="text-xs">{encounter.reasonForVisit ?? '--'}</span>
      </div>
      <div className="flex flex-col gap-2">
        <h2 className="font-semibold">Insurance</h2>
        <div className="flex flex-wrap gap-2">
          {loadingInsurances
            ? <Skeleton className="h-6" />
            : (
              <>
                {insuranceList?.map(({ insuranceName, insuranceId }, index) => (
                  <span
                    key={insuranceId}
                    className="text-xs"
                  >
                    {insuranceName}
                    {index !== (patient?.insurances ?? []).length - 1 && ';'}
                  </span>
                ))}
                {(insuranceList ?? []).length === 0 && (
                  <span className="text-xs text-gray-light">N/A</span>
                )}
              </>
            )}
        </div>
      </div>
      <div className="gap-2">
        <h2 className="font-semibold">Today&apos;s HPI</h2>
        {isFetching && <Skeleton className="h-6" />}
        {!isFetching && hpiElements?.length > 0 && (
          <ul className="list-inside list-disc text-xs gap-1">
            {hpiUpdates?.map((value: string) => (<li key={value}>{value}</li>))}
          </ul>
        )}
        {!isFetching && hpiElements?.length === 0 && (
          <span className="text-xs text-gray-light">HPI not filled out yet</span>
        )}
      </div>
      <div className="gap-2">
        <h2 className="font-semibold">
          {compact([
            'From your last visit with',
            patient?.firstName,
            isNil(lastVisitDate) ? null : `on ${formatDate(lastVisitDate)}`,
          ]).join(' ')}
        </h2>
        {isFetching && <Skeleton className="h-6" />}
        {!isFetching && showPreviousDetails && (
          updates.map(({ label, values = [] }) => values.length > 0 && (
            <Fragment key={label}>
              <p className="text-xs font-semibold my-2">{label}</p>
              <ul className="list-inside list-disc text-xs gap-1 ck-content">
                {values?.map((value: string) => (
                  <VisitDetailsItem key={value} value={value} />
                ))}
              </ul>
            </Fragment>
          ))
        )}
        {!isFetching && !showPreviousDetails && (
          <span className="text-xs text-gray-light">No prior visit notes</span>
        )}
      </div>
    </div>
  );
}
